function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || "";
}

const initializedUploaders = new WeakSet();
const initializedXeroAccountComboboxes = new WeakSet();

function escapeHtml(value) {
  const element = document.createElement("div");
  element.textContent = value || "";
  return element.innerHTML;
}

function uploadToCloudinary(file, config, onProgress) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", config.uploadPreset);
    formData.append("folder", config.folder);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error("Cloudinary returned an invalid response."));
        }
      } else {
        reject(new Error(`Cloudinary upload failed (${xhr.status}).`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Cloudinary upload failed.")));
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${encodeURIComponent(config.cloudName)}/auto/upload`);
    xhr.send(formData);
  });
}

async function recordAttachment(url, file, cloudinaryResult) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({
      attachment: {
        ...cloudinaryResult,
        filename: file.name,
        content_type: file.type,
        source: "manual",
      },
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to record attachment.");
  }

  return data.attachment;
}

function attachmentMarkup(attachment) {
  const filename = escapeHtml(attachment.filename || attachment.public_id || "Attachment");
  const publicId = escapeHtml(attachment.public_id || "");
  const url = escapeHtml(attachment.url || "#");

  return `
    <a href="${url}" target="_blank" rel="noopener" class="flex items-center gap-3 rounded-md border border-[#E4E0D6] p-3 transition-colors hover:bg-[#F6F4EE]/60">
      <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#F6F4EE] text-[#111111]">
        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5A3.375 3.375 0 0 0 10.125 2.25H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      </span>
      <span class="min-w-0">
        <span class="block truncate text-sm font-medium text-[#111111]">${filename}</span>
        <span class="block truncate text-xs text-[#9A9890]">${publicId}</span>
      </span>
    </a>
  `;
}

function updateAttachmentCount(list) {
  const count = list.querySelectorAll("a").length;
  const countElement = document.getElementById("airwallex-attachment-count");
  if (!countElement) return;

  countElement.textContent = `${count} ${count === 1 ? "file" : "files"}`;
}

function xeroAccountLabel(account) {
  if (!account) return "";
  return `${account.name} (${account.code})`;
}

function setupXeroAccountCombobox(root) {
  if (initializedXeroAccountComboboxes.has(root)) return;
  initializedXeroAccountComboboxes.add(root);

  let accounts = [];
  try {
    accounts = JSON.parse(root.dataset.xeroAccounts || "[]");
  } catch {
    accounts = [];
  }

  const hidden = root.querySelector("[data-xero-account-code]");
  const input = root.querySelector("[data-xero-account-query]");
  const toggle = root.querySelector("[data-xero-account-toggle]");
  const menu = root.querySelector("[data-xero-account-menu]");
  const chevron = root.querySelector("[data-xero-account-chevron]");
  let activeIndex = 0;

  if (!hidden || !input || !menu) return;

  const selectedAccount = () => accounts.find((account) => account.code === hidden.value);

  const filteredAccounts = () => {
    const query = input.value.trim().toLowerCase();
    if (!query) return accounts;

    return accounts.filter((account) => {
      const code = account.code.toLowerCase();
      const name = account.name.toLowerCase();
      return code.includes(query) || name.includes(query) || `${name} ${code}`.includes(query);
    });
  };

  const closeMenu = () => {
    menu.classList.add("hidden");
    chevron?.classList.remove("rotate-180");
    input.value = xeroAccountLabel(selectedAccount());
  };

  const chooseAccount = (account) => {
    hidden.value = account?.code || "";
    input.value = xeroAccountLabel(account);
    closeMenu();
  };

  const syncExactMatch = () => {
    const query = input.value.trim().toLowerCase();
    if (!query) {
      hidden.value = "";
      return;
    }

    const match = accounts.find((account) => (
      account.code.toLowerCase() === query || xeroAccountLabel(account).toLowerCase() === query
    ));
    hidden.value = match?.code || "";
  };

  const renderMenu = () => {
    const filtered = filteredAccounts();
    activeIndex = Math.min(activeIndex, Math.max(filtered.length - 1, 0));

    if (filtered.length === 0) {
      menu.innerHTML = '<p class="px-3 py-2 text-[13px] text-[#9A9890]">No matching accounts</p>';
      return;
    }

    menu.innerHTML = filtered.map((account, index) => {
      const active = index === activeIndex;
      const selected = account.code === hidden.value;
      return `
        <button
          type="button"
          data-xero-account-option="${escapeHtml(account.code)}"
          class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition-colors ${active ? "bg-[#F6F4EE] text-[#111111]" : "text-[#3A3A36] hover:bg-[#FAF7EE]"}"
        >
          <span class="min-w-0 truncate">${escapeHtml(account.name)}</span>
          <span class="shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${selected ? "border-[#111111] text-[#111111]" : "border-[#E4E0D6] text-[#9A9890]"}">${escapeHtml(account.code)}</span>
        </button>
      `;
    }).join("");

    menu.querySelectorAll("[data-xero-account-option]").forEach((button, index) => {
      button.addEventListener("mouseenter", () => {
        if (activeIndex === index) return;
        activeIndex = index;
        renderMenu();
      });
      button.addEventListener("mousedown", (event) => {
        event.preventDefault();
        chooseAccount(accounts.find((account) => account.code === button.dataset.xeroAccountOption));
      });
    });
  };

  const openMenu = () => {
    menu.classList.remove("hidden");
    chevron?.classList.add("rotate-180");
    renderMenu();
  };

  input.addEventListener("focus", () => {
    openMenu();
    requestAnimationFrame(() => input.select());
  });

  input.addEventListener("input", () => {
    syncExactMatch();
    activeIndex = 0;
    openMenu();
  });

  input.addEventListener("keydown", (event) => {
    const filtered = filteredAccounts();

    if (event.key === "ArrowDown") {
      event.preventDefault();
      activeIndex = Math.min(activeIndex + 1, Math.max(filtered.length - 1, 0));
      openMenu();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      openMenu();
    } else if (event.key === "Enter" && !menu.classList.contains("hidden") && filtered[activeIndex]) {
      event.preventDefault();
      chooseAccount(filtered[activeIndex]);
    } else if (event.key === "Escape") {
      closeMenu();
      input.blur();
    }
  });

  input.addEventListener("blur", () => {
    window.setTimeout(() => {
      if (!root.contains(document.activeElement)) closeMenu();
    }, 100);
  });

  toggle?.addEventListener("click", () => {
    if (menu.classList.contains("hidden")) {
      input.focus();
      openMenu();
    } else {
      closeMenu();
    }
  });

  document.addEventListener("mousedown", (event) => {
    if (!root.contains(event.target)) closeMenu();
  });
}

function setupTransferUploader(root) {
  if (initializedUploaders.has(root)) return;
  initializedUploaders.add(root);

  const input = root.querySelector("[data-upload-input]");
  const button = root.querySelector("[data-upload-button]");
  const status = root.querySelector("[data-upload-status]");
  const progress = root.querySelector("[data-upload-progress]");
  const progressBar = root.querySelector("[data-upload-progress-bar]");
  const error = root.querySelector("[data-upload-error]");
  const list = document.querySelector("[data-attachments-list]");
  const config = {
    cloudName: root.dataset.cloudName,
    uploadPreset: root.dataset.uploadPreset,
    folder: root.dataset.folder,
    attachmentsUrl: root.dataset.attachmentsUrl,
  };
  let uploading = false;

  const setBusy = (busy) => {
    uploading = busy;
    if (button) button.disabled = busy;
    root.classList.toggle("opacity-70", busy);
  };

  const setStatus = (text, percent) => {
    if (status) status.textContent = text;
    if (typeof percent === "number") {
      progress?.classList.remove("hidden");
      if (progressBar) progressBar.style.width = `${percent}%`;
    }
  };

  const setError = (message) => {
    if (!error) return;
    error.textContent = message || "";
    error.classList.toggle("hidden", !message);
  };

  const addAttachment = (attachment) => {
    if (!list) return;
    list.querySelector("[data-empty-attachments]")?.remove();
    list.insertAdjacentHTML("beforeend", attachmentMarkup(attachment));
    updateAttachmentCount(list);
  };

  const uploadFiles = async (files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (uploading || selectedFiles.length === 0) return;

    setBusy(true);
    setError(null);
    progress?.classList.remove("hidden");

    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        const filePosition = `${index + 1}/${selectedFiles.length}`;
        setStatus(`Uploading ${file.name} (${filePosition})`, 0);

        const cloudinaryResult = await uploadToCloudinary(file, config, (percent) => {
          setStatus(`Uploading ${file.name} (${percent}%)`, percent);
        });

        setStatus(`Recording ${file.name}`, 100);
        const attachment = await recordAttachment(config.attachmentsUrl, file, cloudinaryResult);
        addAttachment(attachment);
      }

      setStatus(`${selectedFiles.length} ${selectedFiles.length === 1 ? "file" : "files"} attached`, 100);
    } catch (err) {
      setError(err.message);
      setStatus("Upload failed", 0);
    } finally {
      setBusy(false);
      if (input) input.value = "";
      setTimeout(() => {
        progress?.classList.add("hidden");
        if (progressBar) progressBar.style.width = "0%";
        if (!error || error.classList.contains("hidden")) {
          setStatus("Drop files here or choose files");
        }
      }, 1400);
    }
  };

  button?.addEventListener("click", () => input?.click());
  input?.addEventListener("change", (event) => uploadFiles(event.target.files));

  root.addEventListener("dragenter", (event) => {
    event.preventDefault();
    root.style.borderColor = "#111111";
    root.style.backgroundColor = "#F6F4EE";
  });

  root.addEventListener("dragover", (event) => {
    event.preventDefault();
  });

  root.addEventListener("dragleave", (event) => {
    event.preventDefault();
    if (!root.contains(event.relatedTarget)) {
      root.style.borderColor = "";
      root.style.backgroundColor = "";
    }
  });

  root.addEventListener("drop", (event) => {
    event.preventDefault();
    root.style.borderColor = "";
    root.style.backgroundColor = "";
    uploadFiles(event.dataTransfer.files);
  });
}

function setupAirwallexTransferUploads() {
  document.querySelectorAll("[data-airwallex-transfer-uploader]").forEach(setupTransferUploader);
}

function setupAirwallexTransferPageControls() {
  setupAirwallexTransferUploads();
  document.querySelectorAll("[data-xero-account-combobox]").forEach(setupXeroAccountCombobox);
}

document.addEventListener("turbo:load", setupAirwallexTransferPageControls);
document.addEventListener("DOMContentLoaded", setupAirwallexTransferPageControls);
